import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock3, Loader2, ExternalLink, Save } from "lucide-react";
import PageHero from "@/components/PageHero";

/**
 * iter181 — Admin Payment Proofs review page.
 *
 * Lists every uploaded payment screenshot with filter chips. The
 * admin can open the image in a new tab (backend-authorised) and
 * change status + attach an internal note. Approving a proof is a
 * signal — the payment ledger is UNCHANGED by any action here.
 */
const STATUSES = ["PENDING REVIEW", "APPROVED", "REJECTED"];

export default function PaymentProofs() {
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payment-proofs${filter ? `?status=${encodeURIComponent(filter)}` : ""}`);
      setRows(data.proofs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="payment-proofs-page">
      <PageHero
        eyebrow="Admin · Payment Proofs"
        title="Payment Screenshots"
        subtitle="Client-uploaded proofs of payment. Approving here is only a review signal — record the actual payment through the existing Finance workflow."
      />

      <div className="flex flex-wrap gap-2" data-testid="pp-filters">
        <FilterChip label="All" active={filter === ""} onClick={() => setFilter("")} />
        {STATUSES.map((s) => (
          <FilterChip key={s} label={s} active={filter === s} onClick={() => setFilter(s)} />
        ))}
      </div>

      {loading ? (
        <div className="card p-8 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-stone-500 text-sm">
          No payment proofs {filter ? `with status ${filter}` : "yet"}.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="pp-list">
          {rows.map((p) => <ProofCard key={p.id} row={p} onChanged={load} />)}
        </ul>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-stone-900 text-white border-stone-900"
          : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
      }`}
      data-testid={`pp-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {label}
    </button>
  );
}

function ProofCard({ row, onChanged }) {
  const [note, setNote] = useState(row.admin_note || "");
  const [saving, setSaving] = useState(null);
  const imgUrl = `${API}/payment-proofs/${row.id}/image`;

  const setStatus = async (status) => {
    setSaving(status);
    try {
      await api.patch(`/payment-proofs/${row.id}`, { status, admin_note: note });
      toast.success(`Marked ${status}`);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(null); }
  };
  const saveNote = async () => {
    setSaving("note");
    try {
      await api.patch(`/payment-proofs/${row.id}`, { status: row.status, admin_note: note });
      toast.success("Note saved");
      await onChanged();
    } catch { toast.error("Save failed"); }
    finally { setSaving(null); }
  };

  const statusBadge = badgeFor(row.status);

  return (
    <li className="card p-4" data-testid={`pp-card-${row.id}`}>
      <div className="flex gap-4 flex-wrap md:flex-nowrap">
        <a
          href={imgUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block shrink-0"
          data-testid={`pp-img-${row.id}`}
        >
          <img
            src={imgUrl}
            alt="Payment screenshot"
            className="w-32 h-32 object-cover rounded-lg border border-stone-200"
          />
        </a>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${statusBadge.cls}`} data-testid={`pp-status-${row.id}`}>
              {statusBadge.icon} {row.status}
            </span>
            <span className="text-xs text-stone-500">
              {new Date(row.uploaded_at).toLocaleString("en-IN")}
            </span>
          </div>
          <div className="mt-2 font-heading text-lg text-stone-900 truncate">
            {row.couple_name || row.client_name || "Client"}
          </div>
          <div className="text-sm text-stone-600">
            {row.milestone_label || "Other / Not sure"}
            {row.milestone_amount ? ` · ₹${Number(row.milestone_amount).toLocaleString("en-IN")}` : ""}
          </div>
          <div className="text-[11px] text-stone-400 mt-1 truncate">
            {row.original_filename} · {(row.size / 1024).toFixed(0)} KB
          </div>
          <textarea
            className="input min-h-[60px] mt-3 text-sm"
            placeholder="Internal note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`pp-note-${row.id}`}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn btn-outline btn-sm" onClick={saveNote} disabled={saving === "note"} data-testid={`pp-save-note-${row.id}`}>
              {saving === "note" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save note
            </button>
            <button type="button" className="btn btn-outline btn-sm !text-emerald-700 !border-emerald-200 hover:!bg-emerald-50" onClick={() => setStatus("APPROVED")} disabled={!!saving} data-testid={`pp-approve-${row.id}`}>
              {saving === "APPROVED" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve
            </button>
            <button type="button" className="btn btn-outline btn-sm !text-red-700 !border-red-200 hover:!bg-red-50" onClick={() => setStatus("REJECTED")} disabled={!!saving} data-testid={`pp-reject-${row.id}`}>
              {saving === "REJECTED" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Reject
            </button>
            <a href={imgUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
              <ExternalLink className="w-3.5 h-3.5" /> Open image
            </a>
          </div>
          {row.reviewed_by && (
            <div className="text-[11px] text-stone-400 mt-2">
              Reviewed by {row.reviewed_by} · {new Date(row.reviewed_at).toLocaleString("en-IN")}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function badgeFor(status) {
  if (status === "APPROVED") return { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} /> };
  if (status === "REJECTED") return { cls: "bg-red-100 text-red-800 border-red-200", icon: <XCircle className="w-3 h-3" strokeWidth={2.4} /> };
  return { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: <Clock3 className="w-3 h-3" strokeWidth={2.4} /> };
}
